import { ChatClient, type ChatClientOptions, type ChatIdentity, type ChatTurn } from 'ProdantixSdk/chat/client';
import { isOpenAt } from 'ProdantixSdk/chat/hours';
import { ChatPanel, type ChatStrings, DEFAULT_APPEARANCE, type FilesKit, type HelpKit, type MessengerAppearance, type TicketsKit } from 'ProdantixSdk/chat/panel';
import { ConversationSocket, type SocketFactory } from 'ProdantixSdk/chat/socket';
import { ConfigError } from 'ProdantixSdk/core/errors';

export type { AppearanceSettings, BubbleFill, BubbleShape, ComposerShape, LauncherContent, LauncherFill, LauncherShape, LauncherSize, MessengerSkin, NamePlacement, StampContent, StampPlacement } from 'ProdantixSdk/chat/appearance';
export { DEFAULT_APPEARANCE_SETTINGS, resolveAppearance } from 'ProdantixSdk/chat/appearance';
export type { ChatAttachment, ChatClientOptions, ChatIdentity, ChatStorage, ChatThread, ChatTurn, ChatUpload, FileFetchLike, FileResponseLike } from 'ProdantixSdk/chat/client';
export { ChatClient } from 'ProdantixSdk/chat/client';
export type { ChatStrings, MessengerAppearance } from 'ProdantixSdk/chat/panel';
export { ChatPanel, DEFAULT_APPEARANCE, panelStyles } from 'ProdantixSdk/chat/panel';

export interface MountChatOptions extends ChatClientOptions {
  /// The element to mount into. Defaults to a fresh `<div>` appended to
  /// `document.body`; a closed shadow root is attached to it either way, so
  /// the host page's CSS never reaches the widget.
  mountPoint?: HTMLElement;
  strings?: Partial<ChatStrings>;
  /// Launcher geometry and accent, as the project configured them. Absent means
  /// the built-in defaults, which is what a caller that has read no config gets.
  appearance?: Partial<MessengerAppearance>;
  /// How often to poll the thread for new turns, in ms. This is the FALLBACK
  /// path: it runs until the socket reports ready and resumes if the socket
  /// falls over, because a socket blocked by a corporate proxy does not report
  /// an error, it goes quiet, which looks exactly like nobody having replied.
  /// `0` disables polling. Default 15s.
  pollIntervalMs?: number;
  /// The `prodantix-ws` origin, from the config endpoint. Absent means the
  /// widget polls and never opens a socket, which is what a caller that has read
  /// no config gets.
  socketHost?: string;
  socketFactory?: SocketFactory;
  /// Fetches the help centre's placements and reader for a project that searches
  /// articles. Absent, the panel draws no help centre.
  help?: () => Promise<HelpKit | null>;
  /// Fetches the paperclip and a turn's files when the panel needs them.
  files?: () => Promise<FilesKit | null>;
  /// Fetches the Tickets tab for a project that takes tickets. Absent, the
  /// panel draws none.
  tickets?: () => Promise<TicketsKit | null>;
}

export interface ChatHandle {
  open(): void;
  close(): void;
  destroy(): void;
  /// Names the person browsing, with the proof their server computed. Refused by
  /// the edge if the proof does not check out, which the page learns through
  /// `onError` rather than by the name quietly never appearing.
  identify(identity: ChatIdentity): void;
}

/// How often the launcher re-asks the project's Hours whether someone is there.
const PRESENCE_REFRESH_MS = 60_000;

/// Mounts the support widget and returns a handle. This is the whole public
/// surface of `@prodantix/sdk/chat`: one call, everything else is internal.
export function mountChat(options: MountChatOptions): ChatHandle {
  const doc = (globalThis as { document?: Document }).document;
  if (!doc) {
    throw new ConfigError('prodantix chat requires a document; mount it in the browser');
  }
  const mountPoint = options.mountPoint ?? doc.createElement('div');
  if (!options.mountPoint) doc.body.append(mountPoint);

  const client = new ChatClient(options);
  const panel = new ChatPanel(mountPoint, client, options.strings, options.appearance, { files: options.files, help: options.help, tickets: options.tickets });

  /* A timeout that re-arms rather than an interval: the poll below is the only
     interval this widget runs, and that is how the changeover tests tell the
     poll is running. */
  const availability = options.appearance?.availability ?? DEFAULT_APPEARANCE.availability;
  let presenceTimer: ReturnType<typeof setTimeout> | undefined;
  const refreshPresence = () => {
    panel.setOpenNow(isOpenAt(availability, new Date()));
    presenceTimer = setTimeout(refreshPresence, PRESENCE_REFRESH_MS);
  };
  refreshPresence();

  /* One updater at a time, and the same `seen` set behind both. A turn that
     arrives on the socket and again on a poll during a changeover is filtered
     here rather than rendered twice, by the mechanism the poll already had. */
  let seen = new Set<string>();
  let timer: ReturnType<typeof setInterval> | undefined;
  let socket: ConversationSocket | null = null;
  const interval = options.pollIntervalMs ?? 15_000;

  const deliver = (turn: ChatTurn) => {
    if (seen.has(turn.id) || turn.authorKind === 'end_user') return;
    seen.add(turn.id);
    panel.onIncomingTurn(turn);
  };

  const poll = async () => {
    if (!client.hasThread) return;
    const thread = await client.load();
    if (!thread) return;
    for (const turn of thread.turns) deliver(turn);
    seen = new Set(thread.turns.map(turn => turn.id));
  };

  const startPolling = () => {
    if (timer || interval <= 0) return;
    timer = setInterval(() => void poll(), interval);
  };

  const stopPolling = () => {
    if (timer) clearInterval(timer);
    timer = undefined;
  };

  /* Opened lazily rather than at mount: the handshake needs a conversation to
     join a room for, and a first-time visitor has none until they send. */
  const connect = () => {
    if (socket || !options.socketHost) return;
    const auth = client.socketAuth;
    if (!auth) return;
    socket = new ConversationSocket({
      auth,
      host: options.socketHost,
      onClosed: retryable => {
        socket = null;
        startPolling();
        /* A close before `ready` was a refusal, and `realtime.rs` refuses
           without saying why on purpose. Reopening would ask the same question
           and get the same silence, so the widget stays on the poll. */
        if (retryable) connect();
      },
      onReady: stopPolling,
      onTurn: deliver,
      socketFactory: options.socketFactory,
    });
    socket.open();
  };

  startPolling();
  connect();

  return {
    close: () => panel.close(),
    destroy: () => {
      stopPolling();
      clearTimeout(presenceTimer);
      socket?.close();
      socket = null;
      panel.close();
      mountPoint.remove();
    },
    identify: (identity: ChatIdentity) => client.identify(identity),
    open: () => {
      void panel.open().then(connect);
    },
  };
}
